import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/query/queryKeys'
import { useConnectApp, useConnections } from '@/features/integrations'
import { navigateConnectWindow, openConnectWindow } from '@/features/integrations/connectPopup'
import { pollConnection } from '@/features/integrations/pollConnection'
import { confirmConnectionUsable } from '@/features/integrations/confirmConnectionUsable'
import { useConnectAnnouncement } from './useConnectAnnouncement'
import { isToolkitLive } from './isToolkitLive'

/**
 * Drive the Connect button on a chat connect card.
 *
 * New cards intentionally have no consent URL. The click starts OAuth through
 * the connections API, so the provider link is always fresh when a user opens
 * an old conversation. Legacy persisted cards may still carry a URL; retain
 * that path until those messages age out.
 *
 * Connected state comes from the live connection list, not local state alone:
 * after a reload the card would otherwise still offer to fix something the
 * user has already fixed. The one exception is `unusable` — an account the
 * provider has told us does not work is ACTIVE in that list, so trusting it
 * would paint a green tick over a broken connection.
 */
export function useConnectCard(block) {
  const [phase, setPhase] = useState('idle')
  const [detail, setDetail] = useState(null)
  const queryClient = useQueryClient()
  const connect = useConnectApp()
  const connections = useConnections()

  const toolkit = block.toolkit ? String(block.toolkit).toLowerCase() : null
  useConnectAnnouncement(phase, toolkit)

  const run = useCallback(() => {
    if (phase === 'working') return
    setPhase('working')

    const finish = (next, why = null) => {
      setDetail(why)
      setPhase(next)
      queryClient.invalidateQueries({ queryKey: ['integrations', 'connections'] })
    }

    // Connected but unusable is not success: announcing it would send the user
    // back to a request that is going to fail again the same way.
    const settle = (r) => {
      if (r?.status !== 'ACTIVE') return finish('idle')
      if (r.healthy === false) return finish('unusable', r.detail)
      return finish('connected')
    }

    if (block.connect_url) {
      const popup = openConnectWindow()
      if (!navigateConnectWindow(popup, block.connect_url)) return finish('error')
      if (!block.connection_id) return finish('idle')
      return void pollConnection({ connectionId: block.connection_id })
        .then((r) => confirmConnectionUsable(r, block.connection_id))
        .then(settle)
        .catch(() => finish('error'))
    }

    connect
      .mutateAsync({ toolkitSlug: toolkit, reconnect: true })
      .then(settle)
      .catch(() => finish('error'))
  }, [block.connect_url, block.connection_id, toolkit, connect, queryClient, phase])

  if (phase === 'unusable') return { state: 'unusable', detail, run }
  const live = phase === 'connected' || isToolkitLive(connections.data, toolkit)
  return { state: live ? 'connected' : phase, detail, run }
}
